import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, Pencil, AlertTriangle } from "lucide-react";
import SearchBar, { applyTextFilter } from "@/components/SearchBar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { PAGES } from "@/lib/permissions";

const ROLE_DEFAULTS = {
  manager: ["dashboard", "warehouses", "receipts", "recipes", "production", "dispatch", "branch_receipts", "transfers", "writeoffs", "history"],
  obsluha: ["branch_receipts", "transfers", "writeoffs"],
};

const ICON_OPTIONS = [
  { value: "coffee", label: "Káva" },
  { value: "croissant", label: "Pečivo" },
  { value: "cake", label: "Dort" },
  { value: "cookie", label: "Sušenka" },
  { value: "pizza", label: "Pizza" },
  { value: "soup", label: "Polévka" },
];

export default function Admin() {
  return (
    <div data-testid="admin-page">
      <PageHeader
        eyebrow="Administrace"
        title="Správa systému"
        description="Uživatelé, pobočky, sklady, suroviny a výrobky. Změny se projeví okamžitě."
      />

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="bg-white border border-[#E5E1D8] rounded-lg p-1 flex flex-wrap gap-1">
          <TabsTrigger value="users" data-testid="admin-tab-users">Uživatelé</TabsTrigger>
          <TabsTrigger value="branches" data-testid="admin-tab-branches">Pobočky</TabsTrigger>
          <TabsTrigger value="warehouses" data-testid="admin-tab-warehouses">Sklady</TabsTrigger>
          <TabsTrigger value="materials" data-testid="admin-tab-materials">Suroviny</TabsTrigger>
          <TabsTrigger value="products" data-testid="admin-tab-products">Výrobky</TabsTrigger>
          <TabsTrigger value="roles" data-testid="admin-tab-roles">Role</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="admin-tab-notifications">Notifikace</TabsTrigger>
          <TabsTrigger
            value="danger"
            data-testid="admin-tab-danger"
            className="data-[state=active]:bg-[#9E47471A] data-[state=active]:text-[#9E4747]"
          >
            Nebezpečná zóna
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-6">
          <UsersAdmin />
        </TabsContent>
        <TabsContent value="branches" className="mt-6">
          <BranchesAdmin />
        </TabsContent>
        <TabsContent value="warehouses" className="mt-6">
          <WarehousesAdmin />
        </TabsContent>
        <TabsContent value="materials" className="mt-6">
          <MaterialsAdmin />
        </TabsContent>
        <TabsContent value="products" className="mt-6">
          <ProductsAdmin />
        </TabsContent>
        <TabsContent value="roles" className="mt-6">
          <RolesAdmin />
        </TabsContent>
        <TabsContent value="notifications" className="mt-6">
          <NotificationsAdmin />
        </TabsContent>
        <TabsContent value="danger" className="mt-6">
          <DangerZone />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Card({ children, title }) {
  return (
    <div className="card-cafe p-6">
      {title && <div className="label-eyebrow mb-4">{title}</div>}
      {children}
    </div>
  );
}

function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [roleTemplates, setRoleTemplates] = useState([]);
  const [form, setForm] = useState({ username: "", email: "", password: "", name: "", role_template_id: "", branch_id: "", quota_kind: "monthly", quota_hours: 160 });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ username: "", email: "", name: "", role: "obsluha", branch_id: "", permissions: {}, password: "", quota_kind: "monthly", quota_hours: 160 });
  const [userSearch, setUserSearch] = useState("");

  const load = async () => {
    const [u, b, t] = await Promise.all([
      api.get("/users"),
      api.get("/branches"),
      api.get("/role-templates"),
    ]);
    setUsers(u.data);
    setBranches(b.data);
    setRoleTemplates(t.data);
    // default selection
    setForm((f) => f.role_template_id ? f : { ...f, role_template_id: t.data.find((x) => x.key === "obsluha")?.id || t.data[0]?.id || "" });
  };
  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.username || !form.password || !form.name) {
      toast.error("Vyplňte uživatelské jméno, jméno a heslo");
      return;
    }
    if (!form.role_template_id) {
      toast.error("Vyberte roli");
      return;
    }
    const tpl = roleTemplates.find((t) => t.id === form.role_template_id);
    try {
      await api.post("/users", {
        username: form.username.trim().toLowerCase(),
        email: form.email ? form.email.trim().toLowerCase() : null,
        password: form.password,
        name: form.name,
        role: tpl?.base_role || "obsluha",
        role_template_id: form.role_template_id,
        branch_id: form.branch_id || null,
        permissions: tpl?.base_role === "admin" ? {} : { ...(tpl?.permissions || {}) },
        quota_kind: form.quota_kind,
        quota_hours: parseFloat(form.quota_hours) || 0,
      });
      toast.success("Uživatel přidán");
      // Necháváme role / pobočku / fond pro rychlé vytvoření dalších uživatelů
      setForm((f) => ({
        ...f,
        username: "",
        email: "",
        password: "",
        name: "",
      }));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    }
  };

  const remove = async (id) => {
    if (!confirm("Smazat uživatele?")) return;
    try {
      await api.delete(`/users/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    }
  };

  const openEdit = (u) => {
    const matchedTpl = roleTemplates.find((t) => t.id === u.role_template_id)
      || roleTemplates.find((t) => t.key === u.role);
    const existing = u.permissions && Object.keys(u.permissions).length > 0
      ? u.permissions
      : (matchedTpl?.permissions || {});
    setEditing(u);
    setEditForm({
      username: u.username || "",
      email: u.email || "",
      name: u.name,
      role: u.role,
      role_template_id: matchedTpl?.id || "",
      branch_id: u.branch_id || "",
      permissions: { ...existing },
      password: "",
      quota_kind: u.quota_kind || "monthly",
      quota_hours: u.quota_hours ?? 160,
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const tpl = roleTemplates.find((t) => t.id === editForm.role_template_id);
      const body = {
        username: editForm.username.trim().toLowerCase(),
        email: editForm.email ? editForm.email.trim().toLowerCase() : null,
        name: editForm.name,
        role: tpl?.base_role || editForm.role,
        role_template_id: editForm.role_template_id || null,
        branch_id: editForm.branch_id || null,
        permissions: (tpl?.base_role || editForm.role) === "admin" ? {} : editForm.permissions,
        quota_kind: editForm.quota_kind || "monthly",
        quota_hours: parseFloat(editForm.quota_hours) || 0,
      };
      if (editForm.password) body.password = editForm.password;
      await api.patch(`/users/${editing.id}`, body);
      toast.success("Uloženo");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    }
  };

  const applyRoleDefaults = (newRole) => {
    const defaults = ROLE_DEFAULTS[newRole] || [];
    const next = {};
    defaults.forEach((k) => (next[k] = true));
    setEditForm({ ...editForm, role: newRole, permissions: next });
  };

  const visibleUsers = useMemo(() => {
    return applyTextFilter(users, userSearch, (u) => [
      u.username,
      u.name,
      u.email,
      u.role,
      branches.find((b) => b.id === u.branch_id)?.name,
    ]);
  }, [users, userSearch, branches]);

  return (
    <div className="space-y-6">
      <Card title="Nový uživatel">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <label className="block">
            <div className="text-xs font-medium uppercase tracking-wider text-[#57534E] mb-1.5">Uživatelské jméno</div>
            <input
              data-testid="user-username"
              placeholder="přihlašovací (např. jan.novak)"
              className="input-cafe w-full"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
            />
          </label>
          <label className="block">
            <div className="text-xs font-medium uppercase tracking-wider text-[#57534E] mb-1.5">Celé jméno</div>
            <input
              data-testid="user-name"
              placeholder="Jan Novák"
              className="input-cafe w-full"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <label className="block">
            <div className="text-xs font-medium uppercase tracking-wider text-[#57534E] mb-1.5">E-mail (nepovinné)</div>
            <input
              data-testid="user-email"
              type="email"
              placeholder="jan@cafecko.cz"
              className="input-cafe w-full"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </label>
          <label className="block">
            <div className="text-xs font-medium uppercase tracking-wider text-[#57534E] mb-1.5">Heslo</div>
            <input
              data-testid="user-password"
              type="password"
              placeholder="heslo"
              className="input-cafe w-full"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </label>
          <label className="block">
            <div className="text-xs font-medium uppercase tracking-wider text-[#57534E] mb-1.5">Role</div>
            <select
              data-testid="user-role"
              className="input-cafe w-full"
              value={form.role_template_id}
              onChange={(e) => setForm({ ...form, role_template_id: e.target.value })}
            >
              <option value="">— vyberte roli —</option>
              {roleTemplates.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <div className="text-xs font-medium uppercase tracking-wider text-[#57534E] mb-1.5">Pobočka</div>
            <select
              data-testid="user-branch"
              className="input-cafe w-full"
              value={form.branch_id}
              onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
            >
              <option value="">— bez pobočky —</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="block">
            <div className="text-xs font-medium uppercase tracking-wider text-[#57534E] mb-1.5">Druh hodinového fondu</div>
            <select
              data-testid="user-quota-kind"
              className="input-cafe w-full"
              value={form.quota_kind}
              onChange={(e) => setForm({ ...form, quota_kind: e.target.value })}
            >
              <option value="monthly">Měsíční</option>
              <option value="weekly">Týdenní</option>
              <option value="daily">Denní</option>
              <option value="variable">Variabilní</option>
            </select>
          </label>
          <label className="block sm:col-span-2">
            <div className="text-xs font-medium uppercase tracking-wider text-[#57534E] mb-1.5">Hodinový fond (h)</div>
            <input
              data-testid="user-quota-hours"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.5"
              className="input-cafe w-full"
              value={form.quota_hours}
              onChange={(e) => setForm({ ...form, quota_hours: e.target.value })}
            />
          </label>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button data-testid="user-create" onClick={create} className="btn-primary">
            <Plus className="w-4 h-4 inline mr-1" /> Přidat uživatele
          </button>
          <p className="text-xs text-[#57534E]">
            Práva pro nového uživatele se nastaví podle role automaticky. Pak je můžete upravit u konkrétního uživatele.
          </p>
        </div>
      </Card>

      <Card title={`Uživatelé (${visibleUsers.length} z ${users.length})`}>
        <SearchBar
          value={userSearch}
          onChange={setUserSearch}
          placeholder="Hledat podle jména, uživ. jména, role nebo pobočky…"
          testid="users-search"
        />
        <div className="overflow-x-auto rounded-md border border-[#E5E1D8]">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F6F0]">
              <tr className="text-left">
                <th className="p-3 text-xs text-[#57534E] font-medium">Uživ. jméno</th>
                <th className="p-3 text-xs text-[#57534E] font-medium">Jméno</th>
                <th className="p-3 text-xs text-[#57534E] font-medium">Role</th>
                <th className="p-3 text-xs text-[#57534E] font-medium">Pobočka</th>
                <th className="p-3 text-xs text-[#57534E] font-medium">Stránky</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.map((u) => {
                const allowed = u.role === "admin"
                  ? "vše"
                  : Object.entries(u.permissions || {}).filter(([_, v]) => v).map(([k]) => k);
                const allowedLabel = u.role === "admin"
                  ? "vše"
                  : Array.isArray(allowed) && allowed.length === 0
                    ? `výchozí dle role`
                    : `${allowed.length} stránek`;
                return (
                  <tr key={u.id} className="border-t border-[#E5E1D8]">
                    <td className="p-3 mono">{u.username}</td>
                    <td className="p-3">{u.name}</td>
                    <td className="p-3 capitalize">{u.role}</td>
                    <td className="p-3 text-[#57534E]">
                      {branches.find((b) => b.id === u.branch_id)?.name || "—"}
                    </td>
                    <td className="p-3 text-[#57534E]">{allowedLabel}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <button
                        data-testid={`user-edit-${u.id}`}
                        onClick={() => openEdit(u)}
                        className="text-xs px-3 py-1.5 rounded border border-[#E5E1D8] hover:bg-[#F9F6F0] inline-flex items-center gap-1 mr-2"
                      >
                        <ShieldCheck className="w-3 h-3" /> Upravit práva
                      </button>
                      <button
                        data-testid={`user-del-${u.id}`}
                        onClick={() => remove(u.id)}
                        className="p-2 rounded hover:bg-[#F9F6F0] text-[#9E4747]"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-2xl bg-white border-[#E5E1D8]" data-testid="user-edit-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>
              Úprava uživatele {editing?.username}
            </DialogTitle>
            <DialogDescription>
              Nastavte uživatelské jméno, roli, pobočku a viditelnost stránek. Admin vždy vidí vše.
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Uživatelské jméno">
                  <input
                    data-testid="edit-username"
                    className="input-cafe w-full"
                    value={editForm.username}
                    onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                  />
                </Field>
                <Field label="Celé jméno">
                  <input
                    data-testid="edit-name"
                    className="input-cafe w-full"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="E-mail (nepovinné)">
                  <input
                    data-testid="edit-email"
                    type="email"
                    className="input-cafe w-full"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  />
                </Field>
                <Field label="Role">
                  <select
                    data-testid="edit-role"
                    className="input-cafe w-full"
                    value={editForm.role_template_id}
                    onChange={(e) => {
                      const tpl = roleTemplates.find((t) => t.id === e.target.value);
                      setEditForm({
                        ...editForm,
                        role_template_id: e.target.value,
                        role: tpl?.base_role || editForm.role,
                        permissions: tpl?.base_role === "admin" ? {} : { ...(tpl?.permissions || {}) },
                      });
                    }}
                  >
                    <option value="">— vyberte —</option>
                    {roleTemplates.map((t) => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Pobočka">
                  <select
                    data-testid="edit-branch"
                    className="input-cafe w-full"
                    value={editForm.branch_id}
                    onChange={(e) => setEditForm({ ...editForm, branch_id: e.target.value })}
                  >
                    <option value="">— bez pobočky —</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Druh hodinového fondu">
                  <select
                    data-testid="edit-quota-kind"
                    className="input-cafe w-full"
                    value={editForm.quota_kind}
                    onChange={(e) => setEditForm({ ...editForm, quota_kind: e.target.value })}
                  >
                    <option value="monthly">Měsíční</option>
                    <option value="weekly">Týdenní</option>
                    <option value="daily">Denní</option>
                    <option value="variable">Variabilní</option>
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Hodinový fond (h)">
                    <input
                      data-testid="edit-quota-hours"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.5"
                      className="input-cafe w-full"
                      value={editForm.quota_hours}
                      onChange={(e) => setEditForm({ ...editForm, quota_hours: e.target.value })}
                    />
                  </Field>
                </div>
              </div>

              <Field label="Reset hesla (nepovinné)">
                <input
                  data-testid="edit-password"
                  type="password"
                  placeholder="ponechte prázdné = beze změny"
                  className="input-cafe w-full"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                />
              </Field>

              <div>
                <div className="label-eyebrow mb-3">Viditelné stránky</div>
                {editForm.role === "admin" ? (
                  <div className="text-sm text-[#57534E] border border-dashed border-[#E5E1D8] rounded-md p-4">
                    Admin má automaticky přístup ke všem stránkám.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {PAGES.map((p) => (
                      <label
                        key={p.key}
                        className="flex items-center gap-3 p-3 border border-[#E5E1D8] rounded-md hover:bg-[#F9F6F0] cursor-pointer"
                      >
                        <input
                          data-testid={`perm-${p.key}`}
                          type="checkbox"
                          className="w-4 h-4 accent-[#3A2D27]"
                          checked={!!editForm.permissions[p.key]}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              permissions: { ...editForm.permissions, [p.key]: e.target.checked },
                            })
                          }
                        />
                        <span className="text-sm">{p.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="px-4 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0]"
            >
              Zrušit
            </button>
            <button data-testid="edit-save" onClick={saveEdit} className="btn-primary">
              Uložit
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-sm font-medium mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function CrudList({ title, items, columns, fields, onCreate, onUpdate, onDelete, onRestore, formState, setFormState, testid, editingId, setEditingId, searchKeys }) {
  const isEditing = !!editingId;
  const [search, setSearch] = useState("");
  const visibleItems = useMemo(() => {
    if (!searchKeys || searchKeys.length === 0) return items;
    return applyTextFilter(items, search, (it) => searchKeys.map((k) => it[k]));
  }, [items, search, searchKeys]);
  return (
    <div className="space-y-6">
      <Card title={isEditing ? "Upravit záznam" : "Přidat nový"}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          {fields.map((f) => {
            if (f.type === "custom") {
              return (
                <div key={f.key} className={f.colSpan || "sm:col-span-2 lg:col-span-4"}>
                  {f.label && (
                    <div className="text-xs font-medium uppercase tracking-wider text-[#57534E] mb-1.5">
                      {f.label}
                    </div>
                  )}
                  {f.render(formState, setFormState)}
                </div>
              );
            }
            const labelText = f.label || f.placeholder || f.key;
            return (
              <label key={f.key} className="block">
                <div className="text-xs font-medium uppercase tracking-wider text-[#57534E] mb-1.5">
                  {labelText}
                </div>
                {f.type === "select" ? (
                  <select
                    data-testid={`${testid}-${f.key}`}
                    className="input-cafe w-full"
                    value={formState[f.key] || ""}
                    onChange={(e) => setFormState({ ...formState, [f.key]: e.target.value })}
                  >
                    <option value="">— {f.placeholder || labelText} —</option>
                    {(typeof f.options === "function" ? f.options(formState) : f.options).map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    data-testid={`${testid}-${f.key}`}
                    type={f.inputType || "text"}
                    step={f.inputType === "number" ? "any" : undefined}
                    placeholder={f.placeholder}
                    className="input-cafe w-full"
                    value={formState[f.key] ?? ""}
                    onChange={(e) => setFormState({ ...formState, [f.key]: e.target.value })}
                  />
                )}
                {f.hint && (
                  <div className="text-[11px] text-[#A8A29E] mt-1">{f.hint}</div>
                )}
              </label>
            );
          })}
          {isEditing ? (
            <div className="flex gap-2">
              <button
                data-testid={`${testid}-save`}
                onClick={() => onUpdate(editingId)}
                className="btn-primary flex-1"
              >
                Uložit
              </button>
              <button
                data-testid={`${testid}-cancel`}
                onClick={() => setEditingId(null)}
                className="px-4 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0]"
              >
                Zrušit
              </button>
            </div>
          ) : (
            <button data-testid={`${testid}-create`} onClick={onCreate} className="btn-primary">
              <Plus className="w-4 h-4 inline mr-1" /> Přidat
            </button>
          )}
        </div>
      </Card>
      <Card title={`${title} (${visibleItems.length} z ${items.length})`}>
        {searchKeys && searchKeys.length > 0 && (
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Hledat v záznamech…"
            testid={`${testid}-search`}
          />
        )}
        {visibleItems.length === 0 ? (
          <div className="text-sm text-[#57534E]">Bez výsledků.</div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-[#E5E1D8]">
            <table className="w-full text-sm">
              <thead className="bg-[#F9F6F0]">
                <tr className="text-left">
                  {columns.map((c) => (
                    <th key={c.key} className="p-3 text-xs text-[#57534E] font-medium">
                      {c.label}
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((it) => {
                  const active = editingId === it.id;
                  const isDel = !!it.deleted;
                  return (
                    <tr
                      key={it.id}
                      className={`border-t border-[#E5E1D8] ${active ? "bg-[#D4A373]/15" : ""} ${isDel ? "opacity-60" : ""}`}
                    >
                      {columns.map((c, ci) => (
                        <td key={c.key} className={`p-3 ${isDel ? "line-through decoration-[#9E4747]/60" : ""}`}>
                          {c.render ? c.render(it) : it[c.key]}
                          {ci === 0 && isDel && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[#9E47471A] text-[#9E4747] no-underline font-medium align-middle">
                              smazáno
                            </span>
                          )}
                        </td>
                      ))}
                      <td className="p-3 text-right whitespace-nowrap">
                        {isDel ? (
                          onRestore && (
                            <button
                              data-testid={`${testid}-restore-${it.id}`}
                              onClick={() => onRestore(it.id)}
                              className="text-xs px-3 py-1.5 rounded border border-[#376A4D] text-[#376A4D] hover:bg-[#376A4D1A] inline-flex items-center gap-1"
                            >
                              Obnovit
                            </button>
                          )
                        ) : (
                          <>
                            <button
                              data-testid={`${testid}-edit-${it.id}`}
                              onClick={() => {
                                const payload = {};
                                fields.forEach((f) => {
                                  if (f.type === "custom") payload[f.key] = it[f.key];
                                  else payload[f.key] = it[f.key] ?? "";
                                });
                                setFormState(payload);
                                setEditingId(it.id);
                                window.scrollTo({ top: 0, behavior: "smooth" });
                              }}
                              className="text-xs px-3 py-1.5 rounded border border-[#E5E1D8] hover:bg-[#F9F6F0] inline-flex items-center gap-1 mr-2"
                            >
                              <Pencil className="w-3 h-3" /> Upravit
                            </button>
                            <button
                              data-testid={`${testid}-del-${it.id}`}
                              onClick={() => onDelete(it.id)}
                              className="p-2 rounded hover:bg-[#F9F6F0] text-[#9E4747]"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function BranchesAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ coffee_grinders_count: 2, coffee_portion_grams: 7, grinder_counters_initial: [] });
  const [editingId, setEditingId] = useState(null);
  const load = async () => setItems((await api.get("/branches")).data);
  useEffect(() => {
    load();
  }, []);

  const reset = () => {
    setForm({ coffee_grinders_count: 2, coffee_portion_grams: 7, grinder_counters_initial: [] });
    setEditingId(null);
  };

  // Synchronize length of grinder_counters_initial with coffee_grinders_count
  const syncedCounters = () => {
    const n = Math.max(1, parseInt(form.coffee_grinders_count) || 2);
    const existing = Array.isArray(form.grinder_counters_initial) ? form.grinder_counters_initial : [];
    const out = [];
    for (let i = 0; i < n; i++) out.push(existing[i] ?? 0);
    return out;
  };

  return (
    <CrudList
      testid="branch"
      title="Pobočky"
      items={items}
      searchKeys={["name", "address"]}
      columns={[
        { key: "name", label: "Název" },
        { key: "address", label: "Adresa" },
        {
          key: "coffee",
          label: "Káva",
          render: (i) => (
            <span className="text-xs text-[#57534E]">
              {i.coffee_grinders_count ?? 2} mlýnky · {i.coffee_portion_grams ?? 7} g/porce
              {Array.isArray(i.grinder_counters_initial) && i.grinder_counters_initial.length > 0 && (
                <> · počítadla start: {i.grinder_counters_initial.join(" / ")}</>
              )}
            </span>
          ),
        },
      ]}
      fields={[
        { key: "name", label: "Název", placeholder: "název pobočky" },
        { key: "address", label: "Adresa", placeholder: "adresa" },
        {
          key: "coffee_grinders_count",
          label: "Počet mlýnků na kávu",
          placeholder: "např. 2",
          inputType: "number",
          hint: "Kolik mlýnků kávy je na pobočce",
        },
        {
          key: "coffee_portion_grams",
          label: "Gramů na porci kávy",
          placeholder: "např. 7",
          inputType: "number",
          hint: "Jedna porce = X gramů (použije se pro výpočet z ranní/večerní hmotnosti)",
        },
        {
          key: "grinder_counters_initial",
          type: "custom",
          label: "Počáteční stavy počítadel",
          colSpan: "sm:col-span-2 lg:col-span-4",
          render: (formState, setFormState) => {
            const n = Math.max(1, parseInt(formState.coffee_grinders_count) || 2);
            const existing = Array.isArray(formState.grinder_counters_initial) ? formState.grinder_counters_initial : [];
            const arr = [];
            for (let i = 0; i < n; i++) arr.push(existing[i] ?? 0);
            return (
              <div>
                <div className="text-[11px] text-[#A8A29E] mb-2">Aktuální stav počítadla na každém mlýnku — bude použit jako startovací hodnota pro první porovnání s odečtem.</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="branch-counter-initial-grid">
                  {arr.map((v, idx) => (
                    <label key={idx} className="block">
                      <div className="text-xs text-[#57534E] mb-1">Mlýnek {idx + 1}</div>
                      <input
                        data-testid={`branch-counter-initial-${idx}`}
                        type="number"
                        step="1"
                        min="0"
                        className="input-cafe w-full"
                        placeholder="0"
                        value={v ?? ""}
                        onChange={(e) => {
                          const next = [...arr];
                          next[idx] = e.target.value === "" ? 0 : parseFloat(e.target.value) || 0;
                          setFormState({ ...formState, grinder_counters_initial: next });
                        }}
                      />
                    </label>
                  ))}
                </div>
              </div>
            );
          },
        },
      ]}
      formState={form}
      setFormState={setForm}
      editingId={editingId}
      setEditingId={(id) => (id === null ? reset() : setEditingId(id))}
      onCreate={async () => {
        if (!form.name) return toast.error("Vyplňte název");
        try {
          const n = parseInt(form.coffee_grinders_count) || 2;
          const counters = syncedCounters().slice(0, n);
          await api.post("/branches", {
            name: form.name,
            address: form.address || "",
            coffee_grinders_count: n,
            coffee_portion_grams: parseFloat(form.coffee_portion_grams) || 7,
            grinder_counters_initial: counters,
          });
          reset();
          toast.success("Přidáno");
          load();
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
      }}
      onUpdate={async (id) => {
        if (!form.name) return toast.error("Vyplňte název");
        try {
          const n = parseInt(form.coffee_grinders_count) || 2;
          const counters = syncedCounters().slice(0, n);
          await api.patch(`/branches/${id}`, {
            name: form.name,
            address: form.address || "",
            coffee_grinders_count: n,
            coffee_portion_grams: parseFloat(form.coffee_portion_grams) || 7,
            grinder_counters_initial: counters,
          });
          reset();
          toast.success("Upraveno");
          load();
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
      }}
      onDelete={async (id) => {
        if (!confirm("Smazat?")) return;
        await api.delete(`/branches/${id}`);
        load();
      }}
    />
  );
}

function WarehousesAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ type: "material" });
  const [editingId, setEditingId] = useState(null);
  const load = async () => setItems((await api.get("/warehouses")).data);
  useEffect(() => {
    load();
  }, []);

  const reset = () => {
    setForm({ type: "material" });
    setEditingId(null);
  };

  return (
    <CrudList
      testid="warehouse"
      title="Sklady"
      items={items}
      searchKeys={["name", "type"]}
      columns={[
        { key: "name", label: "Název" },
        {
          key: "type",
          label: "Typ",
          render: (i) => (i.type === "material" ? "Materiálový" : "Výrobní"),
        },
      ]}
      fields={[
        { key: "name", label: "Název", placeholder: "název" },
        {
          key: "type",
          label: "Typ skladu",
          type: "select",
          placeholder: "typ",
          options: [
            { value: "material", label: "Materiálový" },
            { value: "production", label: "Výrobní" },
          ],
        },
      ]}
      formState={form}
      setFormState={setForm}
      editingId={editingId}
      setEditingId={(id) => (id === null ? reset() : setEditingId(id))}
      onCreate={async () => {
        if (!form.name || !form.type) return toast.error("Vyplňte vše");
        try {
          await api.post("/warehouses", { name: form.name, type: form.type });
          reset();
          toast.success("Přidáno");
          load();
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
      }}
      onUpdate={async (id) => {
        if (!form.name || !form.type) return toast.error("Vyplňte vše");
        try {
          await api.patch(`/warehouses/${id}`, { name: form.name, type: form.type });
          reset();
          toast.success("Upraveno");
          load();
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
      }}
      onDelete={async (id) => {
        if (!confirm("Smazat?")) return;
        await api.delete(`/warehouses/${id}`);
        load();
      }}
    />
  );
}

function MaterialsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ unit: "kg" });
  const [editingId, setEditingId] = useState(null);
  const load = async () => setItems((await api.get("/materials")).data);
  useEffect(() => {
    load();
  }, []);

  const reset = () => {
    setForm({ unit: "kg" });
    setEditingId(null);
  };

  return (
    <CrudList
      testid="material"
      title="Suroviny"
      items={items}
      searchKeys={["name", "unit", "notes"]}
      columns={[
        { key: "name", label: "Název" },
        { key: "unit", label: "Jednotka" },
        { key: "notes", label: "Poznámka" },
      ]}
      fields={[
        { key: "name", label: "Název", placeholder: "název suroviny" },
        {
          key: "unit",
          label: "Jednotka",
          type: "select",
          placeholder: "jednotka",
          options: [
            { value: "kg", label: "kg" },
            { value: "g", label: "g" },
            { value: "l", label: "l" },
            { value: "ml", label: "ml" },
            { value: "ks", label: "ks" },
          ],
        },
        { key: "notes", label: "Poznámka", placeholder: "poznámka" },
      ]}
      formState={form}
      setFormState={setForm}
      editingId={editingId}
      setEditingId={(id) => (id === null ? reset() : setEditingId(id))}
      onCreate={async () => {
        if (!form.name || !form.unit) return toast.error("Vyplňte název a jednotku");
        try {
          await api.post("/materials", { name: form.name, unit: form.unit, notes: form.notes || "" });
          reset();
          toast.success("Přidáno");
          load();
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
      }}
      onUpdate={async (id) => {
        if (!form.name || !form.unit) return toast.error("Vyplňte název a jednotku");
        try {
          await api.patch(`/materials/${id}`, { name: form.name, unit: form.unit, notes: form.notes || "" });
          reset();
          toast.success("Upraveno");
          load();
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
      }}
      onDelete={async (id) => {
        if (!confirm("Smazat?")) return;
        await api.delete(`/materials/${id}`);
        load();
      }}
    />
  );
}

function UnitsEditor({ value, onChange }) {
  const units = Array.isArray(value) ? value : [];
  const set = (next) => onChange(next);
  return (
    <div className="border border-[#E5E1D8] rounded-md p-3 bg-[#F9F6F0]/40">
      <div className="flex items-center justify-between mb-2">
        <div className="label-eyebrow">Alternativní jednotky</div>
        <div className="text-xs text-[#57534E]">Základní jednotka: <strong>1 ks</strong></div>
      </div>
      {units.length === 0 && (
        <div className="text-xs text-[#57534E] py-2">
          Žádné alternativní jednotky. Přidejte např. „balení" = 12 ks, „karton" = 100 ks.
        </div>
      )}
      <div className="space-y-2">
        {units.map((u, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              data-testid={`unit-label-${i}`}
              className="input-cafe flex-1 py-1.5"
              placeholder="název (např. balení)"
              value={u.label || ""}
              onChange={(e) => {
                const next = [...units];
                next[i] = { ...next[i], label: e.target.value };
                set(next);
              }}
            />
            <span className="text-xs text-[#57534E]">= </span>
            <input
              data-testid={`unit-ratio-${i}`}
              type="number"
              inputMode="decimal"
              step="any"
              min="0.001"
              className="input-cafe py-1.5 w-24 text-right"
              placeholder="kolik ks"
              value={u.ratio ?? ""}
              onChange={(e) => {
                const next = [...units];
                next[i] = { ...next[i], ratio: e.target.value === "" ? "" : parseFloat(e.target.value) };
                set(next);
              }}
            />
            <span className="text-xs text-[#57534E]">ks</span>
            <button
              type="button"
              onClick={() => set(units.filter((_, j) => j !== i))}
              className="p-1.5 rounded text-[#9E4747] hover:bg-[#9E47471A]"
              title="Odebrat"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        data-testid="add-unit"
        onClick={() => set([...units, { label: "", ratio: 1 }])}
        className="mt-2 text-sm text-[#3A2D27] inline-flex items-center gap-1 hover:underline"
      >
        <Plus className="w-3.5 h-3.5" /> Přidat jednotku
      </button>
    </div>
  );
}

function ProductsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ icon: "coffee", package_size: 1, package_label: "balení", default_production_unit: "piece", default_dispatch_unit: "piece", units: [], planned_unit_cost: 0, cost_tolerance_pct: 15 });
  const [editingId, setEditingId] = useState(null);
  const load = async () => setItems((await api.get("/products", { params: { include_deleted: true } })).data);
  useEffect(() => {
    load();
  }, []);

  const reset = () => {
    setForm({ icon: "coffee", package_size: 1, package_label: "balení", default_production_unit: "piece", default_dispatch_unit: "piece", units: [], planned_unit_cost: 0, cost_tolerance_pct: 15 });
    setEditingId(null);
  };

  return (
    <CrudList
      testid="product"
      title="Výrobky"
      items={items}
      searchKeys={["name", "icon", "notes"]}
      columns={[
        { key: "name", label: "Název" },
        {
          key: "units",
          label: "Jednotky",
          render: (i) => {
            const extra = (i.units || []).filter((u) => u.label && u.ratio);
            if (extra.length === 0) return "jen ks";
            return extra.map((u) => `${u.label} = ${u.ratio} ks`).join(", ");
          },
        },
        {
          key: "planned_unit_cost",
          label: "Plán. cena",
          render: (i) => {
            const v = Number(i.planned_unit_cost || 0);
            if (!v) return <span className="text-[#A8A29E]">—</span>;
            const tol = Number(i.cost_tolerance_pct ?? 15);
            return (
              <span className="whitespace-nowrap">
                <span className="mono font-medium">{v.toFixed(2)} Kč/ks</span>
                <span className="text-xs text-[#57534E] ml-1">± {tol.toFixed(0)} %</span>
              </span>
            );
          },
        },
        { key: "icon", label: "Ikona" },
        { key: "notes", label: "Poznámka" },
      ]}
      fields={[
        { key: "name", label: "Název výrobku", placeholder: "např. Laskonka" },
        {
          key: "icon",
          label: "Ikona",
          type: "select",
          placeholder: "ikona",
          options: ICON_OPTIONS,
        },
        {
          key: "units",
          label: "Alternativní jednotky",
          type: "custom",
          render: (state, setState) => (
            <UnitsEditor
              value={state.units || []}
              onChange={(v) => setState({ ...state, units: v })}
            />
          ),
        },
        {
          key: "default_production_unit",
          label: "Výchozí jednotka výroby",
          type: "select",
          placeholder: "výchozí jednotka výroby",
          hint: "Která jednotka se předvyplní v dialogu Výroba",
          options: (state) => {
            const extra = (state.units || []).filter(
              (u) => u && u.label && Number(u.ratio) > 0
            );
            return [
              { value: "piece", label: "Výroba: ks (kusy)" },
              ...extra.map((u) => ({
                value: u.label,
                label: `Výroba: ${u.label} (po ${Number(u.ratio)} ks)`,
              })),
            ];
          },
        },
        {
          key: "default_dispatch_unit",
          label: "Výchozí jednotka expedice",
          type: "select",
          placeholder: "výchozí jednotka expedice",
          hint: "Která jednotka se předvyplní v dialogu Expedice",
          options: (state) => {
            const extra = (state.units || []).filter(
              (u) => u && u.label && Number(u.ratio) > 0
            );
            return [
              { value: "piece", label: "Expedice: ks (kusy)" },
              ...extra.map((u) => ({
                value: u.label,
                label: `Expedice: ${u.label} (po ${Number(u.ratio)} ks)`,
              })),
            ];
          },
        },
        {
          key: "planned_unit_cost",
          label: "Plánovaná cena (Kč/ks)",
          placeholder: "např. 12.50",
          hint: "0 = neporovnávat",
          inputType: "number",
        },
        {
          key: "cost_tolerance_pct",
          label: "Tolerance ± %",
          placeholder: "15",
          hint: "Default 15 %. Pokud se skutečná cena odchýlí o víc, zobrazí se varování",
          inputType: "number",
        },
        { key: "notes", label: "Poznámka", placeholder: "poznámka" },

      ]}
      formState={form}
      setFormState={setForm}
      editingId={editingId}
      setEditingId={(id) => (id === null ? reset() : setEditingId(id))}
      onCreate={async () => {
        if (!form.name) return toast.error("Vyplňte název");
        const units = (form.units || [])
          .filter((u) => u.label && u.label.trim() && Number(u.ratio) > 0)
          .map((u) => ({ label: u.label.trim(), ratio: Number(u.ratio) }));
        try {
          await api.post("/products", {
            name: form.name,
            icon: form.icon || "coffee",
            notes: form.notes || "",
            package_size: parseFloat(form.package_size) || 1,
            package_label: form.package_label || "balení",
            default_production_unit: form.default_production_unit || "piece",
            default_dispatch_unit: form.default_dispatch_unit || "piece",
            units,
            planned_unit_cost: Math.max(0, parseFloat(form.planned_unit_cost) || 0),
            cost_tolerance_pct: Math.max(0, parseFloat(form.cost_tolerance_pct) || 15),
          });
          reset();
          toast.success("Přidáno");
          load();
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
      }}
      onUpdate={async (id) => {
        if (!form.name) return toast.error("Vyplňte název");
        const units = (form.units || [])
          .filter((u) => u.label && u.label.trim() && Number(u.ratio) > 0)
          .map((u) => ({ label: u.label.trim(), ratio: Number(u.ratio) }));
        try {
          await api.patch(`/products/${id}`, {
            name: form.name,
            icon: form.icon || "coffee",
            notes: form.notes || "",
            package_size: parseFloat(form.package_size) || 1,
            package_label: form.package_label || "balení",
            default_production_unit: form.default_production_unit || "piece",
            default_dispatch_unit: form.default_dispatch_unit || "piece",
            units,
            planned_unit_cost: Math.max(0, parseFloat(form.planned_unit_cost) || 0),
            cost_tolerance_pct: Math.max(0, parseFloat(form.cost_tolerance_pct) || 15),
          });
          reset();
          toast.success("Upraveno");
          load();
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
      }}
      onDelete={async (id) => {
        if (!confirm("Smazat výrobek? Historické záznamy zůstanou viditelné. Můžete ho kdykoli obnovit.")) return;
        await api.delete(`/products/${id}`);
        load();
      }}
      onRestore={async (id) => {
        await api.post(`/products/${id}/restore`);
        load();
      }}
    />
  );
}


function DangerZone() {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const handleReset = async () => {
    if (confirmText !== "SMAZAT VSE") {
      toast.error("Napište přesně: SMAZAT VSE");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-data", { confirm: "SMAZAT VSE" });
      setResult(data.deleted);
      toast.success("Databáze byla vyčištěna");
      setConfirmText("");
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="danger-zone">
      <div className="card-cafe border-2 border-[#9E4747]/30 p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-[#9E47471A] grid place-items-center shrink-0">
            <AlertTriangle className="w-6 h-6 text-[#9E4747]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-semibold text-[#9E4747]" style={{ fontFamily: "Outfit" }}>
              Vymazat všechna data
            </h3>
            <p className="text-sm text-[#57534E] mt-2 leading-relaxed">
              Odstraní <strong>kompletně</strong> pobočky, sklady, suroviny, výrobky, recepty,
              šarže, výrobní záznamy, expedice, převody, odpisy a docházky.
              Uživatelské účty <strong className="text-[#1C1917]">zůstanou zachovány</strong>.
              Akci <strong>nelze vrátit zpět</strong> — používejte jen pro reset systému před ostrým spuštěním.
            </p>
            <div className="mt-4">
              <button
                data-testid="danger-open-btn"
                onClick={() => { setConfirmText(""); setOpen(true); }}
                className="px-5 py-2.5 rounded-md bg-[#9E4747] text-white font-medium hover:opacity-90 inline-flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" /> Vymazat všechna data
              </button>
            </div>
          </div>
        </div>
      </div>

      {result && (
        <div className="card-cafe p-5" data-testid="danger-result">
          <div className="label-eyebrow text-[#376A4D] mb-2">Hotovo — smazané záznamy</div>
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-sm">
            {Object.entries(result).map(([k, v]) => (
              <li key={k} className="flex justify-between">
                <span className="text-[#57534E]">{k}</span>
                <span className="mono font-medium">{v}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setConfirmText(""); }}>
        <DialogContent className="sm:max-w-md bg-white border-2 border-[#9E4747]/40" data-testid="danger-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#9E4747] flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <AlertTriangle className="w-5 h-5" /> Opravdu vymazat všechna data?
            </DialogTitle>
            <DialogDescription>
              Tato akce nevratně odstraní všechna data kromě uživatelů. Pro potvrzení napište
              přesně text <code className="px-1.5 py-0.5 bg-[#F9F6F0] rounded text-[#9E4747] font-mono">SMAZAT VSE</code>.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-1">
            <input
              data-testid="danger-confirm-input"
              autoFocus
              type="text"
              className="input-cafe w-full font-mono"
              placeholder="SMAZAT VSE"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
            />
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-4 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0]"
            >
              Zrušit
            </button>
            <button
              data-testid="danger-confirm-btn"
              onClick={handleReset}
              disabled={busy || confirmText !== "SMAZAT VSE"}
              className="px-4 py-2 rounded-md bg-[#9E4747] text-white font-medium disabled:opacity-40 inline-flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              {busy ? "Mažu…" : "Ano, vymazat vše"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function RolesAdmin() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const { data } = await api.get("/role-templates");
    setTemplates(data);
  };
  useEffect(() => { load(); }, []);

  const saveTemplate = async (tpl) => {
    try {
      if (tpl.id) {
        await api.patch(`/role-templates/${tpl.id}`, {
          label: tpl.label,
          base_role: tpl.base_role,
          permissions: tpl.permissions,
        });
      } else {
        await api.post("/role-templates", {
          label: tpl.label,
          base_role: tpl.base_role,
          permissions: tpl.permissions,
        });
      }
      toast.success("Uloženo");
      setEditing(null);
      setCreating(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    }
  };

  const removeTemplate = async (id) => {
    if (!confirm("Smazat šablonu role?")) return;
    try {
      await api.delete(`/role-templates/${id}`);
      toast.success("Smazáno");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    }
  };

  const BASE_LABEL = { admin: "Administrátor", manager: "Manažer", obsluha: "Obsluha" };

  return (
    <div data-testid="roles-admin">
      <Card title="Šablony rolí">
        <p className="text-sm text-[#57534E] mb-4">
          Šablona určuje výchozí oprávnění při zakládání uživatele. Vestavěné šablony
          (Admin, Manažer, Obsluha) nelze smazat, ale lze upravit jejich přednastavená
          oprávnění. Vlastní šablony si můžete přidat (např. „Vedoucí směny",
          „Kuchař") a zvolit, ze které ze 3 základních rolí dědí oprávnění na backendu.
        </p>

        <div className="space-y-2">
          {templates.map((t) => (
            <div
              key={t.id}
              data-testid={`role-row-${t.key}`}
              className="border border-[#E5E1D8] rounded-md p-4 flex flex-wrap items-center gap-3 bg-white"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{t.label}</span>
                  {t.is_builtin && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-[#D4A37340] text-[#3A2D27] font-medium uppercase tracking-wider">
                      vestavěná
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#57534E] mt-0.5">
                  Základní role: <strong>{BASE_LABEL[t.base_role]}</strong>
                  {t.base_role !== "admin" && (
                    <> · {Object.values(t.permissions || {}).filter(Boolean).length} oprávnění</>
                  )}
                </div>
              </div>
              <button
                data-testid={`role-edit-${t.key}`}
                onClick={() => setEditing({ ...t, permissions: { ...(t.permissions || {}) } })}
                className="text-xs px-3 py-1.5 rounded border border-[#E5E1D8] hover:bg-[#F9F6F0] inline-flex items-center gap-1"
              >
                <Pencil className="w-3 h-3" /> Upravit
              </button>
              {!t.is_builtin && (
                <button
                  data-testid={`role-del-${t.key}`}
                  onClick={() => removeTemplate(t.id)}
                  className="p-2 rounded hover:bg-[#F9F6F0] text-[#9E4747]"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        <button
          data-testid="role-add-btn"
          onClick={() => setCreating(true)}
          className="mt-4 inline-flex items-center gap-2 btn-primary"
        >
          <Plus className="w-4 h-4" /> Přidat vlastní šablonu
        </button>
      </Card>

      {(editing || creating) && (
        <RoleEditDialog
          template={editing || { label: "", base_role: "obsluha", permissions: {}, is_builtin: false }}
          onCancel={() => { setEditing(null); setCreating(false); }}
          onSave={saveTemplate}
        />
      )}
    </div>
  );
}

function RoleEditDialog({ template, onCancel, onSave }) {
  const [draft, setDraft] = useState({
    id: template.id,
    label: template.label || "",
    base_role: template.base_role || "obsluha",
    permissions: { ...(template.permissions || {}) },
    is_builtin: !!template.is_builtin,
  });
  const isAdminTemplate = draft.is_builtin && draft.base_role === "admin";

  const toggle = (key, val) => {
    setDraft((d) => ({ ...d, permissions: { ...d.permissions, [key]: val } }));
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:max-w-lg bg-white border-[#E5E1D8]" data-testid="role-edit-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>
            {draft.id ? "Upravit šablonu role" : "Nová šablona role"}
          </DialogTitle>
          <DialogDescription>
            {isAdminTemplate
              ? "Administrátor má vždy přístup ke všemu. Lze upravit jen popisek."
              : "Vyberte základní roli (určuje serverovou autorizaci) a zaškrtněte stránky, ke kterým má šablona přístup."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-1">
          <Field label="Název">
            <input
              data-testid="role-label-input"
              className="input-cafe w-full"
              value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              placeholder="např. Vedoucí směny"
            />
          </Field>
          <Field label="Základní role (dědičnost autorizace)">
            <select
              data-testid="role-base-select"
              className="input-cafe w-full disabled:opacity-60"
              value={draft.base_role}
              disabled={draft.is_builtin}
              onChange={(e) => setDraft({ ...draft, base_role: e.target.value })}
            >
              <option value="obsluha">Obsluha</option>
              <option value="manager">Manažer</option>
              <option value="admin">Administrátor</option>
            </select>
            {draft.is_builtin && (
              <p className="text-xs text-[#57534E] mt-1">
                Základní role vestavěné šablony nelze měnit.
              </p>
            )}
          </Field>

          {!isAdminTemplate && (
            <div>
              <div className="label-eyebrow mb-2">Přístup ke stránkám</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-64 overflow-y-auto border border-[#E5E1D8] rounded-md p-3">
                {PAGES.map((p) => (
                  <label
                    key={p.key}
                    className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#F9F6F0] px-2 py-1.5 rounded"
                  >
                    <input
                      data-testid={`role-perm-${p.key}`}
                      type="checkbox"
                      checked={!!draft.permissions[p.key]}
                      onChange={(e) => toggle(p.key, e.target.checked)}
                    />
                    <span>{p.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0]"
          >
            Zrušit
          </button>
          <button
            data-testid="role-save-btn"
            onClick={() => {
              if (!draft.label.trim()) {
                toast.error("Zadejte název");
                return;
              }
              onSave(draft);
            }}
            className="btn-primary"
          >
            Uložit
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// =========================================================================
// NotificationsAdmin — pravidla pro push notifikace
// =========================================================================
function NotificationsAdmin() {
  const [rules, setRules] = useState([]);
  const [eventTypes, setEventTypes] = useState({});
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);

  const emptyRule = {
    id: null,
    name: "",
    event_type: "custom",
    enabled: true,
    config: {},
    recipients: { roles: [], user_ids: [], branches: [] },
    title: "",
    body: "",
  };

  const load = async () => {
    const [r, e, b, u] = await Promise.all([
      api.get("/notification-rules"),
      api.get("/notification-rules/event-types"),
      api.get("/branches"),
      api.get("/users"),
    ]);
    setRules(r.data);
    setEventTypes(e.data);
    setBranches(b.data);
    setUsers(u.data);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!editing) return;
    const payload = {
      name: editing.name,
      event_type: editing.event_type,
      enabled: !!editing.enabled,
      config: editing.config || {},
      recipients: editing.recipients || {},
      title: editing.title || "",
      body: editing.body || "",
    };
    try {
      if (editing.id) {
        await api.patch(`/notification-rules/${editing.id}`, payload);
        toast.success("Uloženo");
      } else {
        await api.post("/notification-rules", payload);
        toast.success("Přidáno");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Smazat pravidlo?")) return;
    await api.delete(`/notification-rules/${id}`);
    await load();
  };

  const fire = async (id) => {
    try {
      const r = await api.post(`/notification-rules/${id}/fire`);
      const d = r.data || {};
      const sent = d.sent || 0;
      if (d.note) toast.info(d.note);
      else if (sent === 0) toast.warning("Nikdo nemá aktivní zařízení / žádní adresáti.");
      else toast.success(`Odesláno na ${sent} zařízení`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    }
  };

  const eventLabel = (t) => eventTypes[t]?.label || t;

  return (
    <div className="space-y-6">
      <Card title="Push notifikace">
        <div className="text-sm text-[#57534E] mb-3">
          Pravidla určují, kdy a komu se odešlou push notifikace do nainstalovaných zařízení.
          Připomenutí odchodu ze směny se spouští automaticky na pozadí (kontrola každou minutu).
        </div>
        <button
          data-testid="notif-new-btn"
          onClick={() => setEditing({ ...emptyRule })}
          className="btn-primary inline-flex items-center gap-1.5 text-sm"
        >
          <Plus className="w-4 h-4" /> Přidat pravidlo
        </button>
      </Card>

      {rules.length === 0 ? (
        <div className="card-cafe p-6 text-sm text-[#57534E]">
          Zatím žádná pravidla. Přidejte první přes tlačítko výše.
        </div>
      ) : (
        <ul className="space-y-3">
          {rules.map((r) => (
            <li
              key={r.id}
              className="card-cafe p-4 sm:p-5 flex items-start gap-4 flex-wrap"
              data-testid={`notif-rule-${r.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-semibold">{r.name}</span>
                  <span
                    className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border"
                    style={{ borderColor: "#D4A37366", color: "#8a6622", background: "#D4A37326" }}
                  >
                    {eventLabel(r.event_type)}
                  </span>
                  {!r.enabled && (
                    <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-[#F9F6F0] text-[#57534E] border border-[#E5E1D8]">
                      Vypnuto
                    </span>
                  )}
                </div>
                {r.event_type === "shift_reminder" && (
                  <div className="text-xs text-[#57534E]">
                    Interval: <strong>{r.config?.hours_after_start ?? 8} h</strong> od nástupu →
                    odchází <em>majiteli aktivní směny</em>
                  </div>
                )}
                {r.event_type === "custom" && (
                  <div className="text-xs text-[#57534E]">
                    {(r.recipients?.roles || []).length > 0 && (
                      <>Role: <strong>{r.recipients.roles.join(", ")}</strong> · </>
                    )}
                    {(r.recipients?.branches || []).length > 0 && (
                      <>
                        Pobočky:{" "}
                        <strong>
                          {r.recipients.branches
                            .map((id) => branches.find((b) => b.id === id)?.name || id)
                            .join(", ")}
                        </strong>{" "}
                        ·{" "}
                      </>
                    )}
                    {(r.recipients?.user_ids || []).length > 0 && (
                      <>
                        Konkrétně:{" "}
                        <strong>
                          {r.recipients.user_ids
                            .map((id) => users.find((u) => u.id === id)?.name || id)
                            .join(", ")}
                        </strong>
                      </>
                    )}
                  </div>
                )}
                {(r.title || r.body) && (
                  <div className="text-xs text-[#57534E] mt-1">
                    <em>„{r.title || "—"}" — {r.body || "—"}</em>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <button
                  data-testid={`notif-fire-${r.id}`}
                  onClick={() => fire(r.id)}
                  className="text-xs px-2 py-1 rounded border border-[#E5E1D8] hover:bg-[#F9F6F0]"
                  title="Poslat teď"
                >
                  Poslat teď
                </button>
                <button
                  data-testid={`notif-edit-${r.id}`}
                  onClick={() => setEditing({ ...r, config: r.config || {}, recipients: r.recipients || { roles: [], user_ids: [], branches: [] } })}
                  className="text-xs px-2 py-1 rounded border border-[#E5E1D8] hover:bg-[#F9F6F0]"
                >
                  Upravit
                </button>
                <button
                  data-testid={`notif-del-${r.id}`}
                  onClick={() => remove(r.id)}
                  className="text-xs px-2 py-1 rounded border border-[#E5E1D8] hover:bg-[#F9F6F0] text-[#9E4747]"
                >
                  Smazat
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Upravit pravidlo" : "Nové pravidlo notifikace"}</DialogTitle>
            <DialogDescription>
              Nastavte, jaká událost spustí push a komu se pošle.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Field label="Název pravidla">
                <input
                  data-testid="notif-form-name"
                  className="input-cafe w-full"
                  placeholder="např. Připomenutí odchodu 10 h"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                />
              </Field>
              <Field label="Typ události">
                <select
                  data-testid="notif-form-event"
                  className="input-cafe w-full"
                  value={editing.event_type}
                  onChange={(e) => setEditing({ ...editing, event_type: e.target.value, config: {} })}
                >
                  {Object.entries(eventTypes).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
                <div className="text-xs text-[#57534E] mt-1">
                  {eventTypes[editing.event_type]?.description}
                </div>
              </Field>
              {/* Config fields */}
              {(eventTypes[editing.event_type]?.config_fields || []).map((cf) => (
                <Field key={cf.key} label={cf.label}>
                  <input
                    data-testid={`notif-form-cfg-${cf.key}`}
                    type={cf.type === "number" ? "number" : "text"}
                    inputMode={cf.type === "number" ? "decimal" : undefined}
                    step={cf.type === "number" ? "any" : undefined}
                    className="input-cafe w-full"
                    value={editing.config?.[cf.key] ?? cf.default ?? ""}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        config: { ...(editing.config || {}), [cf.key]: cf.type === "number" ? parseFloat(e.target.value) || 0 : e.target.value },
                      })
                    }
                  />
                </Field>
              ))}

              {/* Recipients — only pro custom */}
              {editing.event_type === "custom" && (
                <>
                  <Field label="Adresáti — role">
                    <div className="flex flex-wrap gap-1.5">
                      {["admin", "manager", "obsluha"].map((role) => {
                        const active = (editing.recipients?.roles || []).includes(role);
                        return (
                          <button
                            key={role}
                            data-testid={`notif-form-role-${role}`}
                            type="button"
                            onClick={() => {
                              const roles = new Set(editing.recipients?.roles || []);
                              if (active) roles.delete(role); else roles.add(role);
                              setEditing({ ...editing, recipients: { ...editing.recipients, roles: [...roles] } });
                            }}
                            className={`text-xs px-3 py-1.5 rounded-md border ${
                              active ? "bg-[#3A2D27] text-white border-[#3A2D27]" : "border-[#E5E1D8] hover:bg-[#F9F6F0]"
                            }`}
                          >
                            {role}
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                  <Field label="Adresáti — pobočky (nepovinné)">
                    <div className="flex flex-wrap gap-1.5">
                      {branches.map((b) => {
                        const active = (editing.recipients?.branches || []).includes(b.id);
                        return (
                          <button
                            key={b.id}
                            type="button"
                            onClick={() => {
                              const arr = new Set(editing.recipients?.branches || []);
                              if (active) arr.delete(b.id); else arr.add(b.id);
                              setEditing({ ...editing, recipients: { ...editing.recipients, branches: [...arr] } });
                            }}
                            className={`text-xs px-3 py-1.5 rounded-md border ${
                              active ? "bg-[#3A2D27] text-white border-[#3A2D27]" : "border-[#E5E1D8] hover:bg-[#F9F6F0]"
                            }`}
                          >
                            {b.name}
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                </>
              )}

              <Field label="Titulek notifikace (nepovinné, přepíše výchozí)">
                <input
                  data-testid="notif-form-title"
                  className="input-cafe w-full"
                  placeholder="Zpráva se objeví na obrazovce"
                  value={editing.title || ""}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                />
              </Field>
              <Field label="Text zprávy (nepovinné)">
                <input
                  data-testid="notif-form-body"
                  className="input-cafe w-full"
                  placeholder="např. Nezapomeň zapsat odchod v Docházce."
                  value={editing.body || ""}
                  onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                />
              </Field>

              <label className="flex items-center gap-2 select-none cursor-pointer">
                <input
                  data-testid="notif-form-enabled"
                  type="checkbox"
                  checked={!!editing.enabled}
                  onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm">Pravidlo je aktivní</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <button
              onClick={() => setEditing(null)}
              className="px-4 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0]"
            >
              Zrušit
            </button>
            <button
              data-testid="notif-form-save"
              onClick={save}
              className="btn-primary"
            >
              Uložit
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
